// AGC: NewExpense - Create new expense form
// AGC: Has title field, value field, category dropdown, optional notes, and date field (always current)
// AGC: Expenses are associated with the authenticated user account
// AGC: Logs activity to notifications when expense is created
import React, { useState } from 'react'
import { TopBar, GlassCard, Button } from '../shared'
import type { Screen, ExpenseCategory } from '../../types'
import { logExpenseActivity } from '../../services/activityLogger'
import { useAuth } from '../../hooks/useAuth'
import { useTranslation } from '../../hooks/useTranslation'
import { useFinancial } from '../../context'
import toast from 'react-hot-toast'

interface NewExpenseProps {
  onNavigate: (screen: Screen) => void
  onBack: () => void
}

interface ExpenseFormData {
  title: string
  value: string // AGC: String to handle input, converted to number on submit
  category: ExpenseCategory
  notes: string
}

// AGC: Available expense categories
const EXPENSE_CATEGORIES: { value: ExpenseCategory; labelKey: string }[] = [
  { value: 'fuel', labelKey: 'fuel' },
  { value: 'fertilizer', labelKey: 'fertilizer' },
  { value: 'pesticide', labelKey: 'pesticide' },
  { value: 'seeds', labelKey: 'seeds' },
  { value: 'labor', labelKey: 'labor' },
  { value: 'equipment', labelKey: 'equipment' },
  { value: 'maintenance', labelKey: 'maintenance' },
  { value: 'other', labelKey: 'other' },
]

/**
 * AGC: NewExpense - Form to create a new expense
 * AGC: Title field, value field, category dropdown, optional notes, date always set to current date/time
 */
export const NewExpense: React.FC<NewExpenseProps> = ({ onNavigate, onBack }) => {
  const { getAuthToken } = useAuth()
  const { t } = useTranslation()
  // AGC: Use context so newly created expense is instantly cached and visible in FinancesPage
  const { addExpense } = useFinancial()
  const [formData, setFormData] = useState<ExpenseFormData>({
    title: '',
    value: '',
    category: 'other',
    notes: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // AGC: Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Título é obrigatório'
    }

    if (!formData.value.trim()) {
      newErrors.value = 'Valor é obrigatório'
    } else {
      const numValue = parseFloat(formData.value.replace(',', '.'))
      if (isNaN(numValue) || numValue <= 0) {
        newErrors.value = 'Valor deve ser um número maior que zero'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // AGC: Handle create expense
  const handleCreate = async () => {
    if (!validate()) {
      return
    }

    try {
      setIsSubmitting(true)

      // AGC: Convert value to number (handle comma as decimal separator)
      const value = parseFloat(formData.value.replace(',', '.'))
      
      // AGC: Always use current date/time for new expenses
      const currentDate = new Date().toISOString()

      // AGC: Capitalize first letter of title (preserve rest of case)
      const capitalizeFirstLetter = (str: string): string => {
        if (!str) return str
        return str.charAt(0).toUpperCase() + str.slice(1)
      }
      const capitalizedTitle = capitalizeFirstLetter(formData.title.trim())

      // AGC: Create expense via context — updates localStorage cache immediately
      // AGC: so when user returns to FinancesPage it shows the new item without a Firebase round-trip
      const createdExpense = await addExpense({
        title: capitalizedTitle,
        value: value,
        date: currentDate,
        category: formData.category,
        notes: formData.notes.trim() || undefined,
      })

      // AGC: Log activity to notifications (activity log entry, not an alert)
      // AGC: This will appear in the notifications page but NOT in the bell badge
      const token = await getAuthToken()
      await logExpenseActivity(
        capitalizedTitle,
        value,
        'click',
        token,
        createdExpense?.id
      )

      toast.success(t('financial.expenseCreated'), {
        icon: '✅',
        duration: 3000,
      })

      // AGC: Navigate back to finances page
      onNavigate('finances')
    } catch (error) {
      console.error('AGC: Error creating expense:', error)
      toast.error(t('financial.errorCreatingExpense'), {
        icon: '❌',
        duration: 3000,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // AGC: Format value input (handle comma as decimal separator)
  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value
    
    // AGC: Remove any non-numeric characters except comma and dot
    value = value.replace(/[^\d,.]/g, '')
    
    // AGC: Replace comma with dot for parsing, but keep comma for display
    setFormData({ ...formData, value })
  }

  return (
    <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 pb-24">
      {/* AGC: Top Bar */}
      <TopBar 
        title="Nova Despesa"
        onBack={onBack}
      />

      {/* AGC: Form */}
      <div className="px-4 py-4 space-y-4">
        {/* AGC: Title Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Título *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Ex: Combustível, Fertilizantes, etc."
            className={`input ${errors.title ? 'input-error' : ''}`}
          />
          {errors.title && (
            <p className="text-red-500 text-sm mt-1">{errors.title}</p>
          )}
        </div>

        {/* AGC: Value Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Valor *
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
            <input
              type="text"
              value={formData.value}
              onChange={handleValueChange}
              placeholder="0,00"
              className={`input pl-10 ${errors.value ? 'input-error' : ''}`}
              inputMode="decimal"
            />
          </div>
          {errors.value && (
            <p className="text-red-500 text-sm mt-1">{errors.value}</p>
          )}
        </div>

        {/* AGC: Category Dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('financial.category')}
          </label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value as ExpenseCategory })}
            className="input"
          >
            {EXPENSE_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {t(`financial.categories.${cat.labelKey}`)}
              </option>
            ))}
          </select>
        </div>

        {/* AGC: Date Info (read-only, always current) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('financial.expenseDate')}
          </label>
          <GlassCard className="bg-gray-50 dark:bg-slate-800">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {new Date().toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">Data/Hora atual</span>
            </div>
          </GlassCard>
        </div>

        {/* AGC: Notes (optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('financial.expenseNotes')}
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder={t('financial.expenseNotesPlaceholder')}
            rows={3}
            className="input resize-none"
          />
        </div>
      </div>

      {/* AGC: Footer with button - matching dashboard safe area pattern */}
      <div 
        className="fixed left-0 right-0 p-4 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700"
        style={{ bottom: 'max(env(safe-area-inset-bottom), 0px)' }}
      >
        <Button 
          fullWidth 
          onClick={handleCreate}
          disabled={isSubmitting}
        >
          {isSubmitting ? t('financial.saving') : t('financial.saveExpense')}
        </Button>
      </div>
    </div>
  )
}

export default NewExpense

